// First-party page-view tracking for logged-in users. All the decision logic
// lives here so it is unit-testable; RoutePageViewTracker is a thin
// route-change hook. Fire-and-forget by contract: a failed send must never
// surface to the user.
//
// The client sends the path component only. The server derives the listing
// propertyId from it and deliberately stores nothing else — query strings and
// referrers carry PII, so we never send them.

import { baseUrl } from "./index";
import { stripStoredUserTokenSuffix } from "./token";

export const PAGE_VIEW_EXCLUDED_PATH_PREFIXES = [
  "/login",
  "/register",
  "/auth",
  "/sandbox",
  "/resetPassword",
  "/verifyPasswordOtp",
  "/verifyotp",
];

// React 18 StrictMode double-runs effects in dev; the deduper also absorbs
// rapid same-route re-renders in production.
export const PAGE_VIEW_DEDUPE_WINDOW_MS = 2000;

export interface PageViewPayload {
  pathname: string;
}

export function isTrackablePath(pathname: string): boolean {
  return !PAGE_VIEW_EXCLUDED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

// Path component only — propertyId is derived server-side from the pathname,
// and search/referer are never sent (PII).
export function buildPageViewPayload(pathname: string): PageViewPayload {
  return { pathname };
}

// Keyed by token + pathname, not pathname alone: the deduper is module-level
// (to survive StrictMode remounts), so a fast user/tenant switch on the same
// route (user A → logout → user B → /dashboard within the window) must not
// suppress user B's genuine first view. A changed token = a different key.
export function createPageViewDeduper(
  windowMs: number = PAGE_VIEW_DEDUPE_WINDOW_MS
): (token: string, pathname: string, now?: number) => boolean {
  let lastKey: string | null = null;
  let lastAt = 0;

  return (token: string, pathname: string, now: number = Date.now()) => {
    const key = `${token}\n${pathname}`;
    if (key === lastKey && now - lastAt < windowMs) {
      return false;
    }
    lastKey = key;
    lastAt = now;
    return true;
  };
}

export function sendPageView(token: string, payload: PageViewPayload): void {
  const jwt = stripStoredUserTokenSuffix(token);
  fetch(`${baseUrl}/whitelabel/page-views`, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify(payload),
  }).catch(() => {
    // Tracking must never break navigation.
  });
}
